import React, { ForwardedRef, forwardRef, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"
import { useWindowSize } from "../../../hooks"
import { theme } from "../../../theme"
import { ButtonCard } from "../../atoms/button-card"
import { ButtonCardOption } from "./ButtonCardOption"
import { FocusableDiv } from "../../common/FocusableDiv"

export enum ButtonCardGroupFlow {
  Row = "row",
  Column = "column",
}

export type ButtonCardGroupProps<TOptions extends ButtonCardOption[]> = {
  options: TOptions
  className?: string
  disabled?: boolean
  errorMessage?: string
  buttonLabel?: string
  flow?: ButtonCardGroupFlow
  // If true, only allow one of child values to be selected.
  singleValue?: boolean
  // When singleValue is true this is a single value, it must have reference equality with the options; when false this is an array of selected values.
  value?: string | number | string[] | number[] | null
  // When singleValue is true this is a single value; when false an array of selected values. If no value is selected and singleValue is true the value is null; when false an empty array.
  onChange: (value: any) => void
}

const ButtonCardGroupInner = <TOptions extends ButtonCardOption[]>(
  props: ButtonCardGroupProps<TOptions>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const {
    options,
    disabled,
    errorMessage,
    flow = ButtonCardGroupFlow.Column,
    singleValue = false,
    value,
    onChange,
  } = props

  const [windowWidth] = useWindowSize()

  const minWidth = 1117

  const [selectedValue, setSelectedValue] = useState<any>()
  const [isAutoFill, setIsAutoFill] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (
      containerRef?.current &&
      containerRef.current.offsetWidth >= minWidth &&
      options.length >= 3 &&
      flow === ButtonCardGroupFlow.Column
    ) {
      setIsAutoFill(true)
      return
    }

    setIsAutoFill(false)
  }, [windowWidth, containerRef?.current?.offsetWidth])

  useEffect(() => {
    // If no value by default is selected and singleValue is true defaultValue is null; when false it is an empty array.
    const emptyValue = singleValue ? null : []
    const defaultValue = value ? value : emptyValue
    setSelectedValue(defaultValue)
  }, [value])

  const handleChange = (val: string | number, selected: boolean) => {
    let newSelectedValue

    if (singleValue) {
      newSelectedValue = selected ? val : null
    } else {
      if (selected) {
        newSelectedValue = [...selectedValue, val]
      } else {
        newSelectedValue = [...selectedValue.filter((v: string | number) => v !== val)]
      }
    }
    setSelectedValue(newSelectedValue)
    onChange(newSelectedValue)
  }

  const checkIfValueSelected = (val: string | number): boolean => {
    return singleValue ? val === selectedValue : selectedValue?.some((v: string | number) => v === val)
  }

  return (
    <FocusableDiv ref={ref} className={props.className}>
      <Root ref={containerRef}>
        {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <CardGroup $isAutoFill={isAutoFill} $flow={flow}>
          {options.map((option) => (
            <ButtonCard
              key={option.value}
              value={option.value}
              imageUrl={option.imageUrl}
              icons={option.icons}
              description={option.description}
              title={option.title}
              selected={checkIfValueSelected(option.value)}
              buttonLabel={props.buttonLabel}
              onChange={(selected) => handleChange(option.value, selected)}
              disabled={disabled}
              vertical={flow === ButtonCardGroupFlow.Column}
              hasError={!!errorMessage}
            />
          ))}
        </CardGroup>
      </Root>
    </FocusableDiv>
  )
}

export const ButtonCardGroup = forwardRef(ButtonCardGroupInner)

const Root = styled.div`
  display: flex;
  flex-direction: column;
`

const ErrorMessage = styled.div`
  color: ${theme.color.error};
  margin-bottom: ${theme.spacing.l};
  font-size: ${theme.fontSize.s};
  line-height: ${theme.lineHeight.s};
`

const CardGroup = styled.div<{ $flow: ButtonCardGroupFlow; $isAutoFill: boolean }>`
  display: grid;
  width: 100%;
  grid-gap: ${theme.spacing.m};

  ${(p) =>
    p.$flow === ButtonCardGroupFlow.Row &&
    css`
      grid-auto-flow: row;
    `}

  ${({ $flow, $isAutoFill }) =>
    $flow === ButtonCardGroupFlow.Column &&
    css`
      grid-template-columns: ${$isAutoFill === true ? "repeat(3, 367px)" : "repeat(2, 367px)"};
    `}
`
